import {
    Typography,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
} from '@mui/material';
import { CloudUpload, Close, Delete } from '@mui/icons-material';
import { useState, useEffect, useRef, useContext } from 'react';
import { ProjectsContext } from './ProjectsContext';
import {
    VisuallyHiddenInput,
    ImageUploadContainer,
    MultipleImagePreviewContainer,
    ImagePreviewItem,
} from './NewProjectDialog.style';

// Placeholder categories
const categories = [
    'Painting',
    'Fencing',
    'Plumbing',
    'Home Repair',
    'Pressure Washing',
    'Handyman',
];

const backendUrl =
    import.meta.env.MODE === 'development'
        ? `http://${import.meta.env.VITE_BACKEND_URL}`
        : `https://${import.meta.env.VITE_BACKEND_URL_PROD}`;

const NewProjectDialog = () => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);
    const {
        setErrors,
        errors,
        currentProject,
        handleSubmit,
        setDialogOpen,
        dialogOpen,
        formData,
        handleChange,
        loading,
    } = useContext(ProjectsContext);
    const fileInputRef = useRef(null);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        if (!formData.category) {
            newErrors.category = 'Category is required';
        }

        // Require at least one image
        const hasImageFiles =
            formData.imageFiles && formData.imageFiles.length > 0;

        if (!hasImageFiles) {
            newErrors.imageFiles = 'At least one image is required';
        }

        if (!formData.completionDate) {
            newErrors.completionDate = 'Completion date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (formData?.imageFiles && formData?.imageFiles?.length > 0) {
            console.log(formData);
            const processedPreviews = formData.imageFiles
                .map((item) => {
                    // Check if the item is a string (URL path) or a File object
                    if (typeof item === 'string') {
                        // Handle URL path from existing project
                        return {
                            url:
                                import.meta.env.MODE === 'development'
                                    ? item
                                    : `${backendUrl}/media/projects/${item
                                          .split('/')
                                          .pop()}`,
                            file: { name: item.split('/').pop() },
                            isUrl: true,
                        };
                    } else if (item instanceof File) {
                        // Handle File object from new uploads
                        return {
                            url: URL.createObjectURL(item),
                            file: item,
                            isUrl: false,
                        };
                    } else if (item.url) {
                        // Already in the correct format
                        return item;
                    }
                    return null;
                })
                .filter(Boolean);
            setPreviewImages(processedPreviews);
        } else {
            setPreviewImages([]);
        }
    }, [formData]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setSelectedFiles((prevFiles) => [...prevFiles, ...files]);

            // Create preview URLs for the files and add to existing previews
            const newPreviews = [];

            files.forEach((file) => {
                const fileReader = new FileReader();
                fileReader.onload = () => {
                    newPreviews.push({
                        url: fileReader.result,
                        file,
                        isUrl: false,
                    });

                    if (newPreviews.length === files.length) {
                        setPreviewImages((prev) => [...prev, ...newPreviews]);

                        // Store the files directly in the parent component's state
                        const customEvent = {
                            target: {
                                name: 'imageFiles',
                                value: [
                                    ...(formData.imageFiles || []),
                                    ...files,
                                ],
                                type: 'file',
                            },
                        };
                        handleChange(customEvent);
                    }
                };
                fileReader.readAsDataURL(file);
            });
        }
    };

    const removeImage = (index) => {
        const newPreviewImages = [...previewImages];
        const removedImage = newPreviewImages.splice(index, 1)[0];
        setPreviewImages(newPreviewImages);

        // Update the formData.imageFiles array
        if (formData.imageFiles) {
            let newFiles;

            // Handle removing either File objects or path strings
            if (removedImage.isUrl) {
                // Handle string paths
                const pathToRemove = removedImage.url;
                newFiles = formData.imageFiles.filter((item) => {
                    if (typeof item === 'string') {
                        // For development, compare directly
                        if (import.meta.env.MODE === 'development') {
                            return item !== pathToRemove;
                        }
                        // For production, compare by filename
                        return !pathToRemove.includes(item.split('/').pop());
                    }
                    return true; // Keep all File objects
                });
            } else {
                // Handle File objects
                newFiles = formData.imageFiles.filter((item) => {
                    if (item instanceof File) {
                        return (
                            item.name !== removedImage.file.name ||
                            item.size !== removedImage.file.size
                        );
                    }
                    return true; // Keep all string paths
                });
            }

            // Update selectedFiles state for UI
            setSelectedFiles((prev) =>
                prev.filter(
                    (f) =>
                        f.name !== removedImage.file.name ||
                        f.size !== removedImage.file.size
                )
            );

            const fileEvent = {
                target: {
                    name: 'imageFiles',
                    value: newFiles,
                    type: 'file',
                },
            };
            handleChange(fileEvent);
        }
    };

    const clearAllImages = () => {
        setSelectedFiles([]);
        setPreviewImages([]);

        // Reset the file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        const fileEvent = {
            target: {
                name: 'imageFiles',
                value: [],
                type: 'file',
            },
        };
        handleChange(fileEvent);
    };

    return (
        <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {currentProject ? 'Edit Project' : 'Add New Project'}
            </DialogTitle>
            <DialogContent>
                <Box component="form" noValidate sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Title"
                                name="title"
                                value={formData?.title}
                                onChange={handleChange}
                                error={!!errors.title}
                                helperText={errors.title}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl
                                fullWidth
                                margin="normal"
                                error={!!errors.category}
                            >
                                <InputLabel id="category-select-label">
                                    Category
                                </InputLabel>
                                <Select
                                    labelId="category-select-label"
                                    id="category-select"
                                    name="category"
                                    value={formData?.category}
                                    onChange={handleChange}
                                    label="Category"
                                    required
                                >
                                    {categories.map((category) => (
                                        <MenuItem
                                            key={category}
                                            value={category}
                                        >
                                            {category}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.category && (
                                    <Typography variant="caption" color="error">
                                        {errors.category}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Description"
                                name="description"
                                multiline
                                rows={4}
                                value={formData?.description}
                                onChange={handleChange}
                                error={!!errors.description}
                                helperText={errors.description}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <ImageUploadContainer>
                                <Button
                                    component="label"
                                    variant="contained"
                                    startIcon={<CloudUpload />}
                                >
                                    Upload Images
                                    <VisuallyHiddenInput
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                        multiple
                                    />
                                </Button>
                                {selectedFiles.length > 0 && (
                                    <Typography variant="body2">
                                        {selectedFiles.length}{' '}
                                        {selectedFiles.length === 1
                                            ? 'file'
                                            : 'files'}{' '}
                                        selected
                                    </Typography>
                                )}
                                {errors.imageFiles && (
                                    <Typography variant="caption" color="error">
                                        {errors.imageFiles}
                                    </Typography>
                                )}
                            </ImageUploadContainer>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Completion Date"
                                name="completionDate"
                                type="date"
                                value={formData?.completionDate}
                                onChange={handleChange}
                                error={!!errors.completionDate}
                                helperText={errors.completionDate}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth margin="normal">
                                <Typography
                                    component="div"
                                    variant="body1"
                                    sx={{ mb: 1 }}
                                >
                                    Featured Project
                                </Typography>
                                <Select
                                    labelId="featured-select-label"
                                    id="featured-select"
                                    name="featured"
                                    value={formData?.featured}
                                    onChange={handleChange}
                                >
                                    <MenuItem value={true}>Yes</MenuItem>
                                    <MenuItem value={false}>No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {previewImages.length > 0 && (
                            <Grid item xs={12}>
                                <MultipleImagePreviewContainer>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle1">
                                            Image Previews (
                                            {previewImages.length})
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={clearAllImages}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                    <Grid container spacing={2}>
                                        {previewImages.map((image, index) => (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                md={4}
                                                key={index}
                                            >
                                                <ImagePreviewItem>
                                                    <IconButton
                                                        size="small"
                                                        className="delete-button"
                                                        onClick={() =>
                                                            removeImage(index)
                                                        }
                                                    >
                                                        <Close />
                                                    </IconButton>
                                                    <Box
                                                        component="img"
                                                        src={image.url}
                                                        alt={`Project preview ${
                                                            index + 1
                                                        }`}
                                                    />
                                                </ImagePreviewItem>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </MultipleImagePreviewContainer>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button
                    onClick={() => handleSubmit(formData, validateForm)}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewProjectDialog;
